import React from "react";
import { Box, SwipeableDrawer, Typography, useTheme } from "@mui/material";
import { isNative } from "../../lib/platform";
import { overlaySurface } from "../Dashboard/overviewPanel";

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Edge to edge: the content pads itself, so its rules and washes reach the sheet's sides. */
  flush?: boolean;
  /** Unmount the content while closed (the drawer keeps it mounted by default). */
  unmountClosed?: boolean;
  children: React.ReactNode;
}

/**
 * The touch-idiom surface for menus, dialogs and confirmations.
 *
 * Bottom-anchored so it clears the native tab bar without any z-index fight,
 * swipe-to-dismiss for free, and padded past the home indicator so the last
 * row is always reachable. Flat per the design system — the rounded top edge
 * and the backdrop do the separating, never a shadow.
 */
const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  onClose,
  title,
  flush,
  unmountClosed,
  children,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const native = isNative();

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      disableBackdropTransition={native}
      disableDiscovery={native}
      ModalProps={{ keepMounted: !unmountClosed }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          backgroundColor: overlaySurface(isDark),
          backgroundImage: "none",
          boxShadow: "none",
          maxHeight: "85dvh",
          px: flush ? 0 : 2.5,
          pt: 1.5,
          pb: "calc(env(safe-area-inset-bottom, 0px) + 20px)",
        },
      }}
    >
      {/* Grab handle — signals the swipe-to-dismiss affordance */}
      <Box sx={{ display: "flex", justifyContent: "center", flexShrink: 0, mb: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 4,
            borderRadius: 2,
            backgroundColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
          }}
        />
      </Box>

      {title && (
        <Typography
          sx={{
            flexShrink: 0,
            mb: 1,
            px: flush ? 2.5 : 0,
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: theme.palette.text.secondary,
          }}
        >
          {title}
        </Typography>
      )}

      <Box sx={{ overflowY: "auto", minHeight: 0 }}>{children}</Box>
    </SwipeableDrawer>
  );
};

export default BottomSheet;
